import { useEffect } from 'react';
import { api } from '../api/endpoints';
import { announce, installUnlockListeners, spokenStationName } from '../lib/alertSound';

// A PS5 cannot lock itself, so the five-minute warning has to reach a person.
// How the sound is made on each kind of device lives in lib/alertSound.
// Every open dashboard says it out loud — "PS5-1 has 5 minutes left" — once
// per warning, whichever page it is on.
//
// It polls on its own rather than through usePoll, because usePoll stops in a
// background tab and this is exactly the case where nobody is looking at the
// dashboard. Browsers slow timers in hidden tabs; a check every few seconds
// still lands well inside the five minutes.
//
// When the time runs out, a bell rings and the station is named — then again
// every minute, with how far over it is, until staff press "Game stopped —
// mark free". The reminders live in memory only: after a reload the bell rings
// once more straight away, which is the right failure for an unattended game.
//
// Each warning is spoken once, keyed by session and booked length: adding
// time lifts the group back above the line, so the same session warns again
// when the new time runs low. Spoken keys are remembered in the browser so a
// page reload does not repeat a warning already given.

const POLL_MS = 10_000;
const WARNING_SECONDS = 5 * 60;
const TIME_UP_REPEAT_MS = 60_000;
const SPOKEN_KEY = 'hhg.ps5_voice.spoken';

function readSpoken(): string[] {
  try {
    const value = JSON.parse(localStorage.getItem(SPOKEN_KEY) ?? '[]');
    return Array.isArray(value) ? value.filter((key) => typeof key === 'string') : [];
  } catch {
    return [];
  }
}

function writeSpoken(keys: string[]): void {
  try {
    // Only the recent ones matter; old sessions are long over.
    localStorage.setItem(SPOKEN_KEY, JSON.stringify(keys.slice(-50)));
  } catch {
    // Private window or blocked storage: worst case, a reload repeats one.
  }
}

export function Ps5VoiceAlerts() {
  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    const controller = new AbortController();
    // Time-up session -> when it last rang. Cleared once staff mark it free.
    const lastRung = new Map<string, number>();

    const check = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const stations = await api.stations.list(controller.signal);
        if (cancelled) return;
        const spoken = readSpoken();
        let changed = false;
        const stillUp = new Set<string>();
        for (const station of stations) {
          const timeUp = station.time_up;
          if (station.station_type !== 'ps5' || !timeUp || station.active_session) continue;
          stillUp.add(timeUp.session_id);
          const rang = lastRung.get(timeUp.session_id);
          if (rang !== undefined && Date.now() - rang < TIME_UP_REPEAT_MS) continue;
          lastRung.set(timeUp.session_id, Date.now());
          const over = Math.floor((Date.now() - new Date(timeUp.ended_at).getTime()) / 60000);
          const name = spokenStationName(station.name);
          announce({
            bell: true,
            text:
              over < 1
                ? `${name}. Time is up.`
                : `${name}. ${over} minute${over === 1 ? '' : 's'} over time.`,
          });
        }
        for (const sessionId of lastRung.keys()) {
          if (!stillUp.has(sessionId)) lastRung.delete(sessionId);
        }
        for (const station of stations) {
          const session = station.active_session;
          if (station.station_type !== 'ps5' || !session || session.paused) continue;
          const seconds = session.seconds_remaining ?? (session.minutes_remaining ?? 0) * 60;
          if (seconds <= 0 || seconds > WARNING_SECONDS) continue;
          const key = `${session.session_id}:${session.booked_seconds ?? ''}`;
          if (spoken.includes(key)) continue;
          spoken.push(key);
          changed = true;
          const minutes = Math.max(Math.ceil(seconds / 60), 1);
          announce({
            bell: false,
            text: `${spokenStationName(station.name)}. ${minutes} minute${minutes === 1 ? '' : 's'} left.`,
          });
        }
        if (changed) writeSpoken(spoken);
      } catch {
        // The floor page already shows a server error; staying quiet here
        // avoids a second toast for the same outage.
      } finally {
        inFlight = false;
      }
    };

    void check();
    const timer = window.setInterval(check, POLL_MS);
    const removeUnlock = installUnlockListeners();

    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
      removeUnlock();
    };
  }, []);

  return null;
}
